import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { useSelector } from 'react-redux';
import { selectUserToken } from '../../../../../redux/features/user/userSelectors';
import {
  citiesData,
  citiesDataError,
  citiesDataLoading,
} from '../../../../../redux/features/dashboard/dashboardSelectors';
import {
  useAddDistrictMutation,
  useDeleteDistrictMutation,
  useGetDistrictQuery,
  useUpdateDistrictMutation,
} from '../../../../../redux/services/data/districtsApi';

import { FormTemplate } from '../../FormTemplate';
import { InputsTemplate } from '../../../inputs/InputsTemplate';
import { Loader } from '../../../../ui/Loader';
import { DeleteDialogButton } from '../../../DeleteDialogButton';

import {
  initialValues,
  mock,
  validationSchemaDistrict,
} from '../../../../../lib/mocks/add-district-mock';
import {
  getDefaultValues,
  handleResponseError,
} from '../../../../../utils/data';
import {
  errorMessages,
  selectsLocationNames,
} from '../../../../../data/constants';
import { closeDialog, showError } from '../../../../../utils/ui';

export const AddDistrictForm = ({
  formProps = {},
  name = {},
  isEdit = false,
  handleClose = () => {},
  handleResetInput = () => {},
}) => {
  const [error, setError] = useState('');

  const userToken = useSelector(selectUserToken);
  const cities = useSelector(citiesData);
  const citiesLoading = useSelector(citiesDataLoading);
  const citiesError = useSelector(citiesDataError);

  const districtId = useMemo(
    () => formProps?.values?.[name],
    [formProps?.values, name]
  );
  const selectedCityId = useMemo(
    () => formProps?.values?.[selectsLocationNames.city],
    [formProps?.values]
  );

  const {
    data: district,
    isFetching: isDistrictLoading,
    error: districtError,
  } = useGetDistrictQuery(
    { token: userToken, id: districtId },
    { skip: !isEdit || !districtId || !userToken }
  );

  const [addNewDistrict, { isLoading: isAddingLoading, error: addingError }] =
    useAddDistrictMutation();
  const [editDistrict, { isLoading: isEditingLoading, error: editingError }] =
    useUpdateDistrictMutation();
  const [
    deleteDistrict,
    { isLoading: isDeletingLoading, error: deletingError },
  ] = useDeleteDistrictMutation();

  const isLoading =
    citiesLoading ||
    (isEdit && isDistrictLoading) ||
    isAddingLoading ||
    isEditingLoading ||
    isDeletingLoading;

  const selectedCity = useMemo(() => {
    if (!selectedCityId || !cities?.length) return null;
    return cities.find(
      c =>
        c.id.toString() === selectedCityId.toString() ||
        c.value.toString() === selectedCityId.toString()
    );
  }, [selectedCityId, cities]);

  const mockWithData = useMemo(() => {
    if (!cities) return mock;
    return mock.map(item => {
      if (item.name === 'city_id') {
        return { ...item, selectData: cities };
      }
      return item;
    });
  }, [cities]);

  const formInitialValues = useMemo(() => {
    if (isEdit && district) {
      return {
        name: district.name || '',
        city_id: district.city_id || selectedCityId || '',
      };
    }
    return {
      ...initialValues,
      city_id: selectedCityId || '',
    };
  }, [isEdit, district, selectedCityId]);

  const mockWithValues = useMemo(() => {
    if (isEdit && district) {
      return getDefaultValues(
        {
          city_id: selectedCity || {
            value: district.city_id,
            title: 'Unknown',
          },
        },
        mockWithData
      );
    } else if (!isEdit && selectedCity) {
      return getDefaultValues({ city_id: selectedCity }, mockWithData);
    }
    return mockWithData;
  }, [isEdit, district, selectedCity, mockWithData]);

  const handleDelete = async setIsDeleteClick => {
    setIsDeleteClick(false);
    setError('');

    const response = await deleteDistrict({
      token: userToken,
      id: districtId,
    });

    const errorStatus = handleResponseError(
      response?.error,
      deletingError,
      () => closeDialog({ handleClose })
    );

    if (!errorStatus) {
      handleResetInput();
    }

    setError(errorStatus);
  };

  const handleSubmit = async values => {
    setError(null);

    let response;
    let errorStatus;

    if (isEdit) {
      response = await editDistrict({
        token: userToken,
        id: districtId,
        data: values,
      });

      errorStatus = handleResponseError(response?.error, editingError, () =>
        closeDialog({ handleClose })
      );
    } else {
      response = await addNewDistrict({ token: userToken, data: values });

      errorStatus = handleResponseError(response?.error, addingError, () =>
        closeDialog({ handleClose })
      );
    }

    setError(errorStatus);
  };

  useEffect(() => {
    if (citiesError || districtError) {
      setError(errorMessages.wentWrong);
    }
  }, [citiesError, districtError]);

  useEffect(() => {
    showError(error);
  }, [error]);

  return (
    <div className="relative flex flex-col gap-4 w-[260px] sm:w-[420px] exclude-click">
      {!isLoading && (
        <FormTemplate
          initialValues={formInitialValues}
          validationSchema={Yup.object(validationSchemaDistrict)}
          handleSubmitForm={handleSubmit}
          buttonText={'Save'}
          bgColor={'bg-purpleColor border-purpleColor'}
        >
          {formProps => (
            <>
              <InputsTemplate
                formProps={formProps}
                inputsList={mockWithValues}
              />

              {isEdit && (
                <DeleteDialogButton
                  isDeletingLoading={isDeletingLoading}
                  handleDelete={handleDelete}
                />
              )}
            </>
          )}
        </FormTemplate>
      )}

      {isLoading && (
        <div className="flex items-center justify-center w-full h-[152px]">
          <Loader />
        </div>
      )}
    </div>
  );
};
